import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MessageSquare, Send, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useIsMobile } from '../../hooks/useIsMobile';

const G = '#0F5A3A';
const GOLD = '#F5B327';
const IVORY = '#F4F0E6';

const OZ_IMG = '/oz.png';

const SAMPLE_PROMPTS = [
  'What colleges match my profile?',
  'When is FAFSA due for 2027?',
];

const MESSAGES = [
  { from: 'oz', text: "Hi! I'm Oz, your intelligent career and college guide. Based on your Signal Profile, you have a 94% match with UC Berkeley's Computer Science program." },
  { from: 'user', text: 'What scholarships should I apply for?' },
  { from: 'oz', text: "Based on your profile, I found 3 high-match scholarships with a combined value of $28,100. The Coca-Cola Scholars deadline is in 19 days — want me to help you prepare?" },
];

export const OzSection = () => {
  const [inputVal, setInputVal] = useState('');
  const isMobile = useIsMobile();

  return (
    <section style={{ background: '#fff', padding: isMobile ? '48px 0' : '96px 0' }}>
      <div style={{ maxWidth: '1280px', margin: '0 auto', padding: isMobile ? '0 20px' : '0 32px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1.1fr', gap: isMobile ? '32px' : '64px', alignItems: 'center' }}>

          {/* Copy */}
          <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
            <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: GOLD, marginBottom: '12px' }}>Meet Oz</div>
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: isMobile ? '1.75rem' : 'clamp(2rem, 3.2vw, 2.8rem)', fontWeight: 900, color: G, lineHeight: 1.1, letterSpacing: '-0.02em', marginBottom: '16px' }}>
              Intelligent guidance,<br />every step of the way.
            </h2>
            <p style={{ fontSize: '1rem', color: '#6E6A61', lineHeight: 1.75, marginBottom: '28px' }}>
              Oz, your AI Career &amp; College Guide, is your always-on advisor. It knows your profile, understands your goals, and surfaces the exact next action that will move your future forward.
            </p>
            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 32px' }}>
              {['Personalized college &amp; scholarship recommendations','Deadline tracking and smart reminders','Essay assistance and interview prep','Career and major pathway exploration'].map((item, i) => (
                <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '10px', fontSize: '15px', color: '#4A5568' }}>
                  <ChevronRight size={16} color={G} style={{ flexShrink: 0, marginTop: '3px' }} />
                  <span dangerouslySetInnerHTML={{ __html: item }} />
                </li>
              ))}
            </ul>
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
              <Link to="/ask-oz" data-testid="oz-section-cta"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '14px 28px', background: G, color: '#fff', fontWeight: 700, fontSize: '15px', borderRadius: '8px', textDecoration: 'none', boxShadow: `0 8px 24px ${G}30`, transition: 'transform 0.2s, box-shadow 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = `0 14px 36px ${G}40`; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = `0 8px 24px ${G}30`; }}>
                <MessageSquare size={16} /> Ask Oz Anything
              </Link>
              <img src={OZ_IMG} alt="Oz" style={{ height: '48px', width: 'auto', objectFit: 'contain' }} />
            </div>
          </motion.div>

          {/* Chat mockup */}
          <motion.div initial={{ opacity: 0, x: 24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.15 }}
            style={{ background: IVORY, borderRadius: '24px', overflow: 'hidden', boxShadow: '0 24px 60px rgba(15,90,58,0.1)', border: '1px solid rgba(15,90,58,0.08)' }}
            data-testid="oz-chat-mockup">
            <div style={{ padding: '16px 20px', background: G, display: 'flex', alignItems: 'center', gap: '12px' }}>
              <img src={OZ_IMG} alt="Oz" style={{ height: '36px', width: 'auto' }} />
              <div>
                <div style={{ fontSize: '13px', fontWeight: 700, color: '#fff' }}>Oz — Your College &amp; Career Guide</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#4ade80' }} />
                  <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.65)' }}>Online · AI-powered · Always available</span>
                </div>
              </div>
            </div>
            <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '12px', minHeight: '200px' }}>
              {MESSAGES.map((msg, i) => (
                <div key={i} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', flexDirection: msg.from === 'user' ? 'row-reverse' : 'row' }}>
                  {msg.from === 'oz' && <img src={OZ_IMG} alt="Oz" style={{ width: '28px', height: '28px', borderRadius: '50%', objectFit: 'contain', flexShrink: 0 }} />}
                  <div style={{ maxWidth: '78%', padding: '10px 14px', borderRadius: msg.from === 'user' ? '16px 4px 16px 16px' : '4px 16px 16px 16px', background: msg.from === 'user' ? G : '#fff', color: msg.from === 'user' ? '#fff' : '#1a1a1a', fontSize: '13px', lineHeight: 1.55, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                    {msg.text}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ padding: '0 16px 12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {SAMPLE_PROMPTS.map(p => (
                <button key={p} style={{ padding: '5px 12px', borderRadius: '100px', border: '1px solid rgba(15,90,58,0.2)', background: '#fff', fontSize: '11px', color: G, cursor: 'pointer', fontWeight: 600 }}>{p}</button>
              ))}
            </div>
            <div style={{ padding: '12px 16px', background: '#fff', borderTop: '1px solid rgba(15,90,58,0.08)', display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input type="text" placeholder="Ask Oz anything about your future..." value={inputVal} onChange={e => setInputVal(e.target.value)}
                style={{ flex: 1, padding: '10px 14px', borderRadius: '100px', border: '1px solid rgba(15,90,58,0.15)', fontSize: '13px', outline: 'none', background: IVORY, color: '#1a1a1a' }} />
              <button style={{ width: '36px', height: '36px', borderRadius: '50%', background: G, border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                <Send size={14} color="#fff" />
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default OzSection;
